import { useLazyQuery } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { Autocomplete, useDebouncedQuery } from 'components/v2/Autocomplete'
import { IAutocompleteProps } from 'components/v2/Autocomplete/types'
import { GET_VEHICLE_TEMPLATES_AUTOCOMPLETE } from 'features/VehicleTemplates/queries'
import { VehicleTemplateSuggestion } from 'gql/generated/graphql'

const messages = defineMessages({
  error: {
    id: 'vehicleTemplates.autocomplete.error',
    defaultMessage: 'There was an issue while loading your templates'
  },
  empty: {
    id: 'vehicleTemplates.autocomplete.empty',
    defaultMessage: 'No templates found'
  }
})

interface IVehicleTemplateAutocompleteProps extends Omit<IAutocompleteProps, 'onChange' | 'items' | 'getItemLabel'> {
  onChange: (vehicleTemplate: VehicleTemplateSuggestion | null) => void
}

export const VehicleTemplateAutocomplete = ({
  onChange,
  ...props
}: IVehicleTemplateAutocompleteProps): ReactElement => {
  const intl = useIntl()
  const [pattern, setPattern] = useState('')

  const [getVehicleTemplates, { loading, error, data }] = useLazyQuery(GET_VEHICLE_TEMPLATES_AUTOCOMPLETE)
  const { isDebouncing, shouldShowItems } = useDebouncedQuery({
    query: getVehicleTemplates,
    pattern
  })

  const handleChange = (vehicleTemplate: VehicleTemplateSuggestion, { inputValue = '' } = {}): void => {
    setPattern(inputValue)
    if (!isEmpty(vehicleTemplate)) {
      onChange(vehicleTemplate)
    } else {
      onChange(null)
    }
  }

  const shownItems = data?.vehicleTemplateSuggestions.items || []

  return (
    <Autocomplete
      {...props}
      items={shownItems}
      getItemLabel={(vehicleTemplate: VehicleTemplateSuggestion): string =>
        `${vehicleTemplate.name} (${vehicleTemplate.capacity})`
      }
      loading={loading || isDebouncing}
      error={error}
      errorMessage={intl.formatMessage(messages.error)}
      emptyMessage={intl.formatMessage(messages.empty)}
      onChange={handleChange}
      shouldShowItems={shouldShowItems}
    />
  )
}
